/**
 * FollowUpSuggestions — Perplexity-style follow-up question pills
 *
 * Horizontal row of clickable suggestion chips. On click, sends the
 * suggestion text as a prompt via `onAction`. Shows 3-5 max.
 * Subtle styling — not attention-grabbing.
 */
import React from 'react';
import type { FollowUpSuggestionsData } from '../types';
import { ChevronRight } from '../../shared';

// =============================================================================
// COMPONENT
// =============================================================================

interface FollowUpSuggestionsProps {
  data: FollowUpSuggestionsData;
  onAction?: (prompt: string) => void;
}

export const FollowUpSuggestions: React.FC<FollowUpSuggestionsProps> = ({
  data,
  onAction,
}) => {
  const { suggestions } = data;

  if (!suggestions || suggestions.length === 0) return null;

  // Cap at 5 suggestions
  const visible = suggestions.slice(0, 5);

  return (
    <div className="space-y-2 pt-2">
      {/* Label */}
      <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-400/30 px-1">
        Related questions
      </span>

      {/* Pill row */}
      <div className="flex flex-wrap gap-2">
        {visible.map((suggestion, i) => (
          <button
            key={i}
            onClick={() => onAction?.(suggestion)}
            className="group flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-medium text-cyan-400/60 bg-cyan-400/[0.04] border border-cyan-400/15 hover:bg-cyan-400/10 hover:text-cyan-300 hover:border-cyan-400/35 transition-all duration-200 active:scale-[0.97] max-w-full"
          >
            <span className="truncate">{suggestion}</span>
            <ChevronRight className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-cyan-400/40" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default FollowUpSuggestions;
